import { Injectable, OnDestroy } from '@angular/core';
import { DoceboToken, Enrollment, LMSSubscription, User } from '@app/models/_index';
import { getAdditionalFieldValue, getEnrollmentMessage } from '@app/utilities/_index';
import { SigninWithCredentialsOptions, UserClaims } from '@okta/okta-auth-js';
import { Observable, ReplaySubject, Subscription, firstValueFrom, tap } from 'rxjs';

import { UserAdapter } from '@app/adapters/_index';
import { Learning_Type } from '@app/enums/_index';
import { SubscriptionFactory } from '@app/factories/_index';
import { CourseInterface } from '@app/interfaces/_index';
import { environment } from '@environments/environment';
import { AnalyticsService } from '../analytics/analytics.service';
import { ApiService } from '../apiService.service';
import { CourseService } from '../docebo/course.service';
import { EnrollmentService } from '../docebo/enrollment.service';
import { SubscriptionService } from '../docebo/subscription.service';
import { AuthService } from './auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  enrollments: Enrollment[] = [];
  userSubscriptions: LMSSubscription[] = [];

  subs: Subscription[] = [];

  private _doceboUser$: ReplaySubject<User | null> = new ReplaySubject(1);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private enrollmentService: EnrollmentService,
    private subscriptionService: SubscriptionService,
    private courseService: CourseService,
    private subscriptionFactory: SubscriptionFactory,
    private userAdapter: UserAdapter
  ) {
    // TODO: Refactor this to use Observable values instead of Promises
    this.authService.isAuthenticated$
      .pipe(
        tap((isAuthenticated) => {
          if (!isAuthenticated) {
            this._doceboUser$.next(null);
            this._doceboUser$.complete();
            return () => 'unauthenticated';
          }
          return this.apiService
            .get(`${environment.docebo.url}/manage/v1/user/session`)
            .then(async (response: any) => {
              return this.apiService.get(
                `${environment.docebo.url}/manage/v1/user/${response?.id}`
              );
            })
            .then(async (nextResponse: any) => {
              // TODO: Refactor
              if (nextResponse?.user_data && nextResponse?.additional_fields) {
                const user = this.userAdapter.adapt(nextResponse);
                this._doceboUser$.next(user);
                this._doceboUser$.complete();
                this.clearEnrollments();
                await this.loadSubscriptions(); // Do not delete. Must detect what subscriptions are has to allow new or check existing enrollments.
                await this.loadEnrollments(user);
                if (user?.email) {
                  const companyName = getAdditionalFieldValue(
                    environment.docebo.additional_fields.company_name,
                    user
                  );
                  this.analyticsService.identify(user.email, companyName);
                }
              }
            })
            .catch((err) => {
              console.error('Unable to load user from Docebo', { cause: err });
              this._doceboUser$.next(null);
              this._doceboUser$.complete();
            });
        })
      )
      .subscribe({
        next: (response) => {
          console.log(`User Service: Next data ${response}`);
        },
        error: (error) => {
          console.log(`User Service Error: ${error}`);
        },
        complete: () => {
          console.log('User is Authenticated is completed');
        },
      });
  }

  /**
   * @returns Okta User claims Observable
   */
  get claims$(): Observable<UserClaims> {
    return this.authService.claims$;
  }

  /**
   * @returns Is Authenticated boolean Observable
   */
  get isAuthenticated$(): Observable<boolean> {
    return this.authService.isAuthenticated$;
  }

  /**
   * @returns Docebo User Observable
   */
  get doceboUser$(): Observable<User> {
    return this._doceboUser$.asObservable();
  }

  /**
   * Authenticate the user with custom Okta login page
   * @param options has username and password
   * @returns Okta AuthTransaction promise
   */
  async signInWithCredentials(options: SigninWithCredentialsOptions): Promise<void> {
    return this.authService.signInWithCredentials(options);
  }

  /**
   * Sign In with Okta Redirect
   */
  signInWithRedirect(originalUri): Promise<void> {
    return this.authService.signInWithRedirect(originalUri);
  }

  /**
   * Sign Out of Okta
   * @param redirectUrl
   */
  signOut(): void {
    this.authService.signOut();
  }

  /**
   * Return Docebo Access Token Observable
   */
  getDoceboAccessToken(): Observable<DoceboToken> {
    return this.authService.getDoceboAccessToken();
  }

  /**
   *
   * Clear user enrollments
   */
  private clearEnrollments(): void {
    this.enrollments = [];
  }

  /**
   * TODO: Refactor
   * Update existing user enrollments to get the latest
   * enrollment changes.
   * @param user - incoming user object
   */
  async loadEnrollments(user: User): Promise<void> {
    if (user) {
      user.enrollments = [];
      return this.enrollmentService
        .getEnrollments(user.id)
        .then((enrollments: Enrollment[]) => {
          if (enrollments) {
            this.enrollments = this.loadEnrollmentMessage(enrollments);
            // Load enrollments to user model
            // Clear enrollments to force pipe to detect changes and update any
            // place that is calling this user service to force updated enrollment states.
            if (this.enrollments && this.enrollments.length > 0) {
              user.enrollments.push(...this.enrollments.slice());
            }
          }
        })
        .catch((err) => {
          console.log('Unable to load enrollments,', err);
        })
        .finally(() => {});
    } else {
      return Promise.reject('User must be defined');
    }
  }

  /**
   * TODO: Refactor
   * Update Dashboard enrollment message by status
   * @param enrollments - course enrollments
   */
  private loadEnrollmentMessage(enrollments: Enrollment[]): Enrollment[] {
    if (enrollments && enrollments.length > 0) {
      // NOTE: Learning Plan enrollments is same thing as Subscription
      const learningPlanEnrollments =
        enrollments.filter((enrollment) => enrollment.type === Learning_Type.SUBSCRIPTION) || [];
      const nonLearningPlanningEnrollments =
        enrollments.filter((enrollment) => enrollment.type !== Learning_Type.SUBSCRIPTION) || [];

      // Set enrollment status for learning plan enrollments.
      if (learningPlanEnrollments && learningPlanEnrollments.length > 0) {
        learningPlanEnrollments.forEach((enrollment) => {
          // NOTE: Enrollment does not contain learning types by default, but can have a valid
          // enrollment status. The get enrollment message utility handles this default case.
          enrollment.enrolledMessage = getEnrollmentMessage(
            enrollment.learningType,
            enrollment.status
          );
        });
      }
      // Else for non-learning enrollments get course additional fields.
      if (nonLearningPlanningEnrollments && nonLearningPlanningEnrollments.length > 0) {
        const fields = environment.docebo.additional_fields;
        nonLearningPlanningEnrollments.forEach((enrollment) => {
          if (enrollment && enrollment.id) {
            this.subs.push(
              // TODO: Call course adapter to use course interace
              this.courseService.getCourse(enrollment.id).subscribe((course: CourseInterface) => {
                if (course.data.additional_fields && course.data.additional_fields.length > 0) {
                  course.data.additional_fields.forEach((field) => {
                    // NOTE: Allow == type coercion
                    // eslint-disable-next-line
                    if (field.id == fields.learning_type) {
                      if (!!field.value) {
                        enrollment.learningType = field.value;
                        enrollment.enrolledMessage = getEnrollmentMessage(
                          enrollment.learningType,
                          enrollment.status
                        );
                      }
                    }
                  });
                }
              })
            );
          }
        });
      }

      // Return joined arrays
      return learningPlanEnrollments.concat(nonLearningPlanningEnrollments);
    }
  }

  /**
   * Do not delete. Maintain the current user subscription states here.
   * Load Active Subscriptions and activate them assigned to that user.
   */
  private async loadSubscriptions(): Promise<void> {
    const currentUser = await firstValueFrom(this.doceboUser$);
    if (currentUser) {
      return this.subscriptionService.getActiveSubscriptions(currentUser).then((subscriptions) => {
        if (subscriptions.length > 0) {
          this.userSubscriptions = [];
          subscriptions.forEach((subscription: string) => {
            if (subscription) {
              this.userSubscriptions.push(this.subscriptionFactory.getSubscription(subscription));
            }
          });
          // Add user subscriptions to user, if user is signed in and has active subscriptions
          if (currentUser) {
            currentUser.activeSubscriptions =
              this.userSubscriptions && this.userSubscriptions.length > 0
                ? this.userSubscriptions.slice()
                : [];
          }
        }
      });
    } else {
      return Promise.reject('Current user is not defined');
    }
  }

  /**
   * Logout and redirect of Beyond
   * @param url
   */
  logoutAndRedirectTo(url: string): void {
    this.authService.logoutAndRedirectTo(url);
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
